import type { AbsenceReason } from './valueObjects/absenceReason';
import type { AbsenceReasonDetailInfection } from './valueObjects/absenceReasonDetailInfection';
import type { AbsenceReasonDetailSick } from './valueObjects/absenceReasonDetailSick';
import type { AttendDateTime } from './valueObjects/attendDateTime';
import type { AttendStatus } from './valueObjects/attendStatus';
import type { AttendanceDate } from './valueObjects/attendanceDate';
import type { ChildId } from './valueObjects/childId';
import type { LeaveDateTime } from './valueObjects/leaveDateTime';
import type { MemberId } from './valueObjects/memberId';
import type { Memo } from './valueObjects/memo';
import type { ParentId } from './valueObjects/parentId';
import type { SheetJsonPub, SheetJsonPvt } from '@/types/domain/attendance/entities/sheetEntity';

import { EntityPub } from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/** 欠席プロパティ */
export type AbsencePropsType = {
  /** 理由 */
  reason: AbsenceReason,
  /** 理由病欠 */
  reasonDetailSick?: AbsenceReasonDetailSick,
  /** 理由感染症 */
  reasonDetailInfection?: AbsenceReasonDetailInfection,
  /** メモ */
  memo: Memo,
  /** メンバーID */
  memberId: MemberId,
}

/** 出席タイプ */
export type AttendType = {
  /** メンバーID */
  memberId: MemberId,
  /** 出席日時 */
  attendDateTime: AttendDateTime,
}

/** 早退 */
export type LeaveType = {
  /** メンバーID */
  memberId: MemberId,
  /** 早退日時 */
  leaveDateTime: LeaveDateTime,
}

/** 出席簿プロパティ非公開 */
export type SheetPropsPvt = {
  /** 出席日 */
  attendanceDate: AttendanceDate,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 出席ステータス */
  attendStatus: AttendStatus,
  /** 欠席 */
  absence?: AbsencePropsType,
  /** 出席 */
  attend?: AttendType,
  /** 早退 */
  leave?: LeaveType,
}

/** 出席簿プロパティ公開 */
export type SheetPropsPub = Record<string, never>

/** 出席簿Entity */
export class SheetEntity extends EntityPub<
  SheetPropsPvt,
  SheetJsonPvt,
  SheetPropsPub,
  SheetJsonPub
> {
  toJsonPubData(): SheetJsonPub {
    return {};
  }

  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  get deleteKey(): string {
    return this.getFutureYear(this.attendanceDate.value, 2).toJSON();
  }

  toJsonData(): SheetJsonPvt {
    return {
      attendanceDate: this.attendanceDate.toStringDate(),
      parentId: this.parentId.value,
      childId: this.childId.value,
      attendStatus: this.attendStatus.value,
      absence: this.absence ? {
        reason: this.absence.reason.value,
        reasonDetailSick:
          this.absence.reasonDetailSick ? this.absence.reasonDetailSick.value : undefined,
        reasonDetailInfection:
          this.absence.reasonDetailInfection ? this.absence.reasonDetailInfection.value : undefined,
        memo: this.absence.memo.value,
        memberId: this.absence.memberId.value,
      } : undefined,
      attend: this.attend ? {
        memberId: this.attend.memberId.value,
        attendDateTime: this.attend.attendDateTime.value.toJSON(),
      } : undefined,
      leave: this.leave ? {
        memberId: this.leave.memberId.value,
        leaveDateTime: this.leave.leaveDateTime.value.toJSON(),
      } : undefined,
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryAttendanceSheetEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    const ymd = this.attendanceDate.toStringDate();
    return `${ymd}#${this.parentId.value}_${this.childId.value}`;
  }

  /** サブキー */
  get subKey(): string | null {
    const ymd = this.attendanceDate.toStringDate();
    return `${this.parentId.value}_${this.childId.value}#${ymd}`;
  }

  /** 親子ID */
  get parentChildId(): string {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /** 出席日 */
  get attendanceDate(): AttendanceDate {
    return this._value.attendanceDate;
  }

  /** 出席日 */
  set attendanceDate(attendanceDate: AttendanceDate) {
    this._value.attendanceDate = attendanceDate;
  }

  /** 親ID */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /** 親ID */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /** 子ID */
  get childId(): ChildId {
    return this._value.childId;
  }

  /** 子ID */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }

  /**
   * 出席ステータス
   */
  get attendStatus(): AttendStatus {
    return this._value.attendStatus;
  }

  /**
   * 出席ステータス
   */
  set attendStatus(attendStatus: AttendStatus) {
    this._value.attendStatus = attendStatus;
  }

  /** 欠席 */
  get absence(): AbsencePropsType | undefined {
    return this._value.absence;
  }

  /** 欠席 */
  set absence(absence: AbsencePropsType | undefined) {
    this._value.absence = absence;
  }

  /** 出席 */
  get attend(): AttendType | undefined {
    return this._value.attend;
  }

  /** 出席 */
  set attend(attend: AttendType | undefined) {
    this._value.attend = attend;
  }

  /** 早退 */
  get leave(): LeaveType | undefined {
    return this._value.leave;
  }

  /** 早退 */
  set leave(leave: LeaveType | undefined) {
    this._value.leave = leave;
  }

  /**
   * 現在のステータス取得
   * @returns ステータス
   */
  getCurrentStatus() {
    if (this.attendStatus.value === '欠席') {
      return '欠席';
    }
    if (this.leave) {
      return '降園';
    }
    return '出席';
  }

  /**
   * 出席テキスト取得
   * @returns 出席テキスト
   */
  getAttendText() {
    let start = '';
    if (this.attend) {
      start = this.attend.attendDateTime.toStringTimeDisplay(false);
    }
    let end = '';
    if (this.leave) {
      end = this.leave.leaveDateTime.toStringTimeDisplay(false);
    }
    return `登録: ${start} - ${end}`;
  }
}
